/**
 * Описание колонок таблицы (§4.2, §4.4, §4.9).
 *
 * Один источник правды: код, заголовок, ширина в px, выравнивание, группа,
 * закрепление, тип редактора и поле, из которого колонка читает значение.
 * Ширины фиксированы (§4.2): резиновых колонок нет, поэтому sticky-смещения
 * считаются один раз из суммы и в рантайме не пересчитываются.
 */
import type { ChannelCode, FieldDefinition, FieldScope, FieldType } from '@shared/types.ts'
import { tableTexts as t } from '../texts.table.js'
import { applyOrder, type ColumnOrder } from './columnOrder.js'

/**
 * Колонки, которые нельзя сдвинуть с места: чекбокс и название бренда всегда
 * первые, потому что на них держится закреплённая слева зона.
 */
export const FIXED_HEAD = ['gutter', 'brand'] as const

function headRank(column: { code: string }): number {
  const index = (FIXED_HEAD as readonly string[]).indexOf(column.code)
  return index === -1 ? FIXED_HEAD.length : index
}

// ───────────────────────────── типы описания ─────────────────────────────────

/**
 * Код смыслового блока колонок. Встроенные перечислены явно, но тип открыт:
 * пользовательское поле может завести свой блок — его код приходит из
 * настроек поля, а название едет рядом в `ColumnDef.groupTitle`.
 */
export type BuiltInGroupCode =
  | 'base' | 'talk' | 'site' | 'priorities' | 'wg' | 'seo' | 'context' | 'fields'
export type ColumnGroupCode = BuiltInGroupCode | (string & {})
export type ColumnAlign = 'left' | 'center' | 'right'
export type ColumnPin = 'left' | 'right' | null

/** Чем ячейка рисуется. */
export type CellRole =
  | 'gutter' | 'brand' | 'presence' | 'tier' | 'enum' | 'text' | 'url' | 'bool'
  | 'number' | 'date' | 'user' | 'multiselect'
  | 'comments' | 'comment' | 'groupFold'

/** Чем ячейка правится (§5.2). `none` — нередактируемая. */
export type CellEditor =
  | 'none' | 'enum' | 'text' | 'url' | 'bool' | 'number' | 'date' | 'user' | 'multiselect'

/** Откуда колонка читает значение. Чтение всегда через *Cell из draft-overlay. */
export type ColumnSource =
  | { kind: 'none' }
  | { kind: 'brand'; field: 'name' | 'categoriesUnclear' }
  | { kind: 'channel'; field: ChannelField }
  | { kind: 'custom'; fieldId: number; code: string; scope: FieldScope }

export type ChannelField =
  | 'visibility' | 'tier' | 'url' | 'stockDisplay' | 'oos'
  | 'delivery' | 'installation' | 'warranty'
  | 'seoPriority' | 'contextPriority' | 'note'

export interface ColumnDef {
  code: string
  /** Заголовок в шапке — ровно то, что видно человеку. */
  title: string
  /** Полное имя для скринридера и тултипа, если заголовок — глиф или аббревиатура. */
  srTitle?: string
  width: number
  align: ColumnAlign
  group: ColumnGroupCode
  pinned: ColumnPin
  role: CellRole
  editor: CellEditor
  source: ColumnSource
  /** Набор справочника для редактора `enum`. */
  enumSet?: string
  /** Колонку можно отключить в панели «Колонки». */
  optional: boolean
  /** В шапке появляется подпись канала: «URL (РОЗНИЦА)» (§4.6). */
  channelLabelled: boolean
  /** Применима к строке бренда / строке категории (§4.3: применимость по scope поля). */
  inBrandRow: boolean
  inCategoryRow: boolean
  /** Числовой моноширинный набор (tnum). */
  tnum?: boolean
  /** Вес при дележе свободной ширины: таблица всегда занимает экран целиком. */
  flex?: number
  /** Колонка открывает группу — на ней рисуется вертикальная граница группы. */
  groupStart?: boolean
  /** Минимальная ширина при растягивании. */
  minWidth?: number
  /** Подсказка в шапке. */
  hint?: string
  /** Пользовательское поле, если колонка из конструктора (§4.4). */
  field?: FieldDefinition
  /** Имя своего блока: у встроенных берётся из GROUP_TITLE, у заведённых — отсюда. */
  groupTitle?: string
  /** Группа свёрнута: колонка-индикатор вместо набора (§4.2). */
  foldedGroup?: ColumnGroupCode
}

/**
 * Названия блоков по их кодам: встроенные плюс заведённые пользовательскими
 * полями. Собирается из каталога, потому что имя своего блока живёт в
 * настройках поля, а не в коде.
 */
export function groupTitles(catalog: readonly ColumnDef[]): Record<string, string> {
  const out: Record<string, string> = { ...GROUP_TITLE }
  /* Своё название перебивает встроенное: его задали руками, значит оно и
     нужно — хоть у заведённого блока, хоть у «Уровня сервиса». */
  for (const column of catalog) {
    if (column.groupTitle) out[column.group] = column.groupTitle
  }
  return out
}

/** Порядок блоков: встроенные в своём порядке, заведённые — следом. */
export function groupOrder(catalog: readonly ColumnDef[]): ColumnGroupCode[] {
  const out: ColumnGroupCode[] = [...GROUP_ORDER]
  for (const column of catalog) {
    if (!out.includes(column.group)) out.push(column.group)
  }
  return out
}

/* «Уровень сервиса» — самая правая группа: ею пользуются реже, чем
   приоритетами, и она не должна отжимать SEO и контекст за край экрана. */
/* «Комментарии» идут сразу за «Брендом»: по ним ведут работу, и читают их
   вместе с названием, а не в конце строки. */
export const GROUP_ORDER: readonly ColumnGroupCode[] = ['base', 'talk', 'site', 'priorities', 'seo', 'context', 'fields', 'wg']

export const GROUP_TITLE: Record<string, string> = {
  base: t.group.base,
  talk: t.group.talk,
  site: t.group.site,
  priorities: t.group.priorities,
  wg: t.group.wg,
  seo: t.group.seo,
  context: t.group.context,
  fields: t.group.fields,
}

/** Аббревиатура свёрнутой группы: в заголовке вертикального текста не бывает (§4.2). */
export const GROUP_SHORT: Partial<Record<ColumnGroupCode, string>> = { wg: t.group.wgShort }

export const GROUP_HINT: Partial<Record<ColumnGroupCode, string>> = { wg: t.hint.wg }

/** Свёрнуто по умолчанию: White Glove заполнен на 0 % в обоих каналах (§0). */
/* Группы развёрнуты: «Уровень сервиса» — это три колонки исходной таблицы
   (доставка, установка, гарантия), и схлопывать их в одну было неверно. */
export const DEFAULT_COLLAPSED_GROUPS: readonly ColumnGroupCode[] = []

/** §4.9 Карта зон ответственности: area → поля зоны, первое становится «моей колонкой». */
export const AREA_FIELDS: Record<string, readonly string[]> = {
  commerce: ['tier', 'visibility'],
  seo: ['seoPriority'],
  marketing: ['contextPriority'],
  site: ['url', 'stockDisplay', 'oos', 'categoriesUnclear'],
  // admin и собственник отвечают за всё, персональной колонки у них нет (§10.4).
  admin: [],
}

/** §4.2 Порядок выбивания колонок при нехватке ширины окна. */
/**
 * Порядок, в котором колонки уступали место при нехватке ширины. Сейчас не
 * используется: автоматическое скрытие убрано, таблица просто прокручивается.
 * Оставлено как описание приоритета колонок — если скрытие вернут, начинать
 * нужно с примечания, а не с приоритетов.
 */
export const EVICTION_ORDER: readonly string[] = [
  'stockDisplay', 'oos', 'warranty', 'installation', 'delivery',
]

/** §4.4 Карта тип поля → ширина и выравнивание. */
const FIELD_WIDTH: Record<FieldType, number> = {
  text: 160, textarea: 200, number: 80, date: 100, bool: 44,
  url: 170, select: 120, multiselect: 180, user: 140,
}
const FIELD_ALIGN: Record<FieldType, ColumnAlign> = {
  text: 'left', textarea: 'left', number: 'right', date: 'left', bool: 'center',
  url: 'left', select: 'left', multiselect: 'left', user: 'left',
}
const FIELD_ROLE: Record<FieldType, CellRole> = {
  text: 'text', textarea: 'text', number: 'number', date: 'date', bool: 'bool',
  url: 'url', select: 'enum', multiselect: 'multiselect', user: 'user',
}
const FIELD_EDITOR: Record<FieldType, CellEditor> = {
  text: 'text', textarea: 'text', number: 'number', date: 'date', bool: 'bool',
  url: 'url', select: 'enum', multiselect: 'multiselect', user: 'user',
}

// ───────────────────────────── каталог колонок ───────────────────────────────

const GUTTER: ColumnDef = {
  code: 'gutter', title: '', srTitle: t.column.gutter, width: 56, align: 'center',
  group: 'base', pinned: 'left', role: 'gutter', editor: 'none', source: { kind: 'none' },
  optional: false, channelLabelled: false, inBrandRow: true, inCategoryRow: true,
}

/** Полный каталог колонок канала, в порядке групп (§4.2). */
/**
 * Каталог колонок канала.
 *
 * `titles` — переименования, которые команда задала в настройках таблицы:
 * названия встроенных колонок и блоков зашиты в интерфейс, и без этой карты
 * «Приоритет Контекст» нельзя было назвать по-своему.
 */
export function buildColumnCatalog(
  channel: ChannelCode,
  fields: FieldDefinition[],
  titles: {
    columns?: Record<string, string>
    groups?: Record<string, string>
    /** Колонки, переставленные в другой блок: { код колонки: код блока }. */
    moved?: Record<string, string>
  } = {},
): ColumnDef[] {

  const base: ColumnDef[] = [
    GUTTER,
    {
      code: 'brand', title: t.column.brand, width: 220, minWidth: 180, align: 'left', group: 'base',
      // Переименование живёт в карточке бренда (§5.8): в сетке клик по имени
      // открывает карточку, а не редактор — случайных переименований не бывает.
      pinned: 'left', role: 'brand', editor: 'none', source: { kind: 'brand', field: 'name' },
      optional: false, channelLabelled: false, inBrandRow: true, inCategoryRow: true,
    },
    // ── САЙТ ──
    {
      code: 'visibility', title: t.column.visibility, srTitle: t.column.visibilityFull, width: 148,
      align: 'left', group: 'base', pinned: null, role: 'enum', editor: 'enum',
      source: { kind: 'channel', field: 'visibility' }, enumSet: 'visibility',
      optional: true, channelLabelled: true, inBrandRow: true, inCategoryRow: false,
    },
    {
      code: 'url', title: t.column.url, width: 220, minWidth: 180, align: 'left', group: 'site', pinned: null,
      role: 'url', editor: 'url', source: { kind: 'channel', field: 'url' },
      optional: true, channelLabelled: true, inBrandRow: true, inCategoryRow: false,
    },
    {
      code: 'stockDisplay', title: t.column.stockDisplay, width: 164, flex: 1, align: 'left', group: 'wg',
      pinned: null, role: 'enum', editor: 'enum', source: { kind: 'channel', field: 'stockDisplay' },
      enumSet: 'stock_display', optional: true, channelLabelled: true,
      inBrandRow: true, inCategoryRow: false,
    },
    {
      code: 'oos', title: t.column.oos, width: 148, align: 'left', group: 'wg', pinned: null,
      role: 'enum', editor: 'enum', source: { kind: 'channel', field: 'oos' }, enumSet: 'oos',
      optional: true, channelLabelled: true, inBrandRow: true, inCategoryRow: false,
    },
    // ── ПРИОРИТЕТЫ ──
    {
      code: 'tier', title: t.column.tier, srTitle: t.column.tierFull, width: 76, align: 'left',
      group: 'base', pinned: null, role: 'tier', editor: 'enum',
      source: { kind: 'channel', field: 'tier' }, enumSet: 'tier',
      optional: true, channelLabelled: true,
      // Тир — единственное канальное поле, активное и в строке категории (§4.3).
      inBrandRow: true, inCategoryRow: true,
    },
    {
      code: 'seoPriority', title: t.column.seoPriority, width: 180, flex: 1, align: 'left', group: 'seo',
      pinned: null, role: 'enum', editor: 'enum', source: { kind: 'channel', field: 'seoPriority' },
      enumSet: 'seo_priority', optional: true, channelLabelled: true,
      inBrandRow: true, inCategoryRow: false,
    },
    {
      code: 'contextPriority', title: t.column.contextPriority, width: 190, flex: 1, align: 'left',
      group: 'context', pinned: null, role: 'enum', editor: 'enum',
      source: { kind: 'channel', field: 'contextPriority' }, enumSet: 'context_priority',
      optional: true, channelLabelled: true, inBrandRow: true, inCategoryRow: false,
    },
    // ── WHITE GLOVE ──
    {
      code: 'delivery', title: t.column.delivery, width: 124, align: 'left', group: 'wg',
      pinned: null, role: 'text', editor: 'text', source: { kind: 'channel', field: 'delivery' },
      optional: true, channelLabelled: true, inBrandRow: true, inCategoryRow: false,
    },
    {
      code: 'installation', title: t.column.installation, width: 124, align: 'left', group: 'wg',
      pinned: null, role: 'text', editor: 'text', source: { kind: 'channel', field: 'installation' },
      optional: true, channelLabelled: true, inBrandRow: true, inCategoryRow: false,
    },
    {
      code: 'warranty', title: t.column.warranty, width: 124, align: 'left', group: 'wg',
      pinned: null, role: 'text', editor: 'text', source: { kind: 'channel', field: 'warranty' },
      optional: true, channelLabelled: true, inBrandRow: true, inCategoryRow: false,
    },
    /* Группы «Прочее» больше нет. «Примечание» дублировало приоритет, а
       «Пропущено» и «Категории» были счётчиками, по которым ничего не делают.
       Значок комментариев остался — он про разговор вокруг бренда, поэтому
       стоит в группе «Бренд», рядом с названием. */
    {
      code: 'comments', title: '', srTitle: t.column.comments, width: 64, align: 'center',
      group: 'base', pinned: null, role: 'comments', editor: 'none', source: { kind: 'none' },
      optional: true, channelLabelled: false, inBrandRow: true, inCategoryRow: false, tnum: true,
    },
    /* Последний комментарий прямо в строке. Бренды ведут именно так: пишут
       напротив строки, не проваливаясь в карточку, — значит, и читать
       написанное нужно оттуда же. Колонка широкая: это текст, а не значок. */
    {
      code: 'lastComment', title: t.column.lastComment, srTitle: t.column.lastComment,
      width: 340, align: 'left', group: 'talk', pinned: null, role: 'comment',
      editor: 'none', source: { kind: 'none' },
      optional: true, channelLabelled: false, inBrandRow: true, inCategoryRow: false,
    },
  ]

  const columnTitles = titles.columns ?? {}
  const groupNames = titles.groups ?? {}
  const movedTo = titles.moved ?? {}
  return [...base, ...customColumns(fields)].map((column) => {
    const own = columnTitles[column.code]
    /* Чекбокс и название бренда переставлять нельзя: на них держится
       закреплённая слева зона таблицы. */
    const moved = (FIXED_HEAD as readonly string[]).includes(column.code)
      ? undefined
      : movedTo[column.code]
    const group = moved ?? column.group
    const groupName = groupNames[group]
    if (!own && !groupName && !moved) return column
    return {
      ...column,
      group,
      ...(own ? { title: own, srTitle: column.srTitle ? own : undefined } : null),
      ...(groupName ? { groupTitle: groupName } : null),
    }
  })
}

/** §4.4 Пользовательские поля приходят в группу «ПОЛЯ». */
function customColumns(fields: FieldDefinition[]): ColumnDef[] {
  return fields
    .filter((field) => field.status === 'active')
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
    .map((field) => ({
      code: `field:${field.code}`,
      title: field.name,
      width: field.config.width ?? FIELD_WIDTH[field.type],
      align: FIELD_ALIGN[field.type],
      group: (field.config.group ?? 'fields') as ColumnGroupCode,
      ...(field.config.groupTitle ? { groupTitle: field.config.groupTitle } : null),
      pinned: null,
      role: FIELD_ROLE[field.type],
      editor: FIELD_EDITOR[field.type],
      source: { kind: 'custom' as const, fieldId: field.id, code: field.code, scope: field.scope },
      enumSet: field.config.enumSet,
      optional: true,
      channelLabelled: field.scope === 'brand_channel',
      inBrandRow: field.scope !== 'brand_category',
      inCategoryRow: field.scope === 'brand_category',
      tnum: field.type === 'number' || field.type === 'date',
      hint: field.hint ?? undefined,
      field,
    }))
}

/** Набор по умолчанию для канала (§4.2): в Опте колонки URL нет. */
/**
 * Набор по умолчанию — восемь колонок (docs/VISUAL-REFERENCE.md §6).
 * Двадцать колонок разом читались как шум; остальное включается в «Колонках».
 */
const DEFAULT_SET: readonly string[] = [
  'brand', 'visibility', 'tier', 'url',
  'delivery', 'installation', 'warranty',
  'seoPriority', 'contextPriority',
  /* Комментарий — не справочное поле, а рабочая заметка: по ней и ведут
     бренды. Поэтому он в наборе по умолчанию, а не в «Колонках». */
  'lastComment',
]

export function defaultVisible(channel: ChannelCode, catalog: ColumnDef[]): string[] {
  return catalog
    .filter((column) => {
      if (!column.optional) return true
      /* Колонка «Ссылка» показывается в обоих каналах: в Опте адреса были
         пусты у всех 186 брендов, поэтому её прятали, — теперь там стоят
         страницы оптового ЛК. */
      /* Проверяем происхождение колонки, а не её блок: заведённое поле может
         стоять в любом блоке, в том числе в своём. */
      if (column.field) return column.field.showByDefault === true
      return DEFAULT_SET.includes(column.code)
    })
    .map((column) => column.code)
}

/**
 * Делёж свободной ширины между гибкими колонками.
 * Таблица обязана занимать экран целиком: пустая треть справа выглядела поломкой.
 */
function stretchToWidth(columns: ColumnDef[], available: number): ColumnDef[] {
  if (available <= 0) return columns
  const total = sumWidth(columns)
  const spare = available - total
  if (spare <= 0) return columns

  const weights = columns.reduce((sum, column) => sum + (column.flex ?? 0), 0)
  if (weights === 0) return columns

  let given = 0
  const out = columns.map((column) => {
    if (!column.flex) return column
    const add = Math.floor((spare * column.flex) / weights)
    given += add
    return { ...column, width: column.width + add }
  })
  // Остаток от округления отдаём самой «резиновой» колонке, чтобы сумма сошлась.
  const rest = spare - given
  if (rest > 0) {
    const widest = out.reduce<ColumnDef | null>(
      (best, column) => (column.flex && (!best || column.flex > (best.flex ?? 0)) ? column : best), null,
    )
    if (widest) widest.width += rest
  }
  return out
}

// ───────────────────────────── раскладка ─────────────────────────────────────

export interface ResolveOptions {
  channel: ChannelCode
  catalog: ColumnDef[]
  /** Явный выбор человека (URL `cols=`). null — набор по умолчанию. */
  chosen: readonly string[] | null
  collapsedGroups: ReadonlySet<ColumnGroupCode>
  /** Поля зоны ответственности смотрящего (§4.9). */
  areaFields: readonly string[]
  /** `roles.area` смотрящего: у admin зона «все», персональной колонки нет (§10.4). */
  area: string | null
  /** Одна дополнительная колонка, закреплённая слева человеком (§4.2). */
  pinnedLeftExtra: string | null
  /** Доступная ширина тела таблицы в px. */
  available: number
  /** Личный порядок групп и колонок внутри них (перетаскивание в «Колонках»). */
  order?: ColumnOrder | null
}

export interface ResolvedColumns {
  columns: ColumnDef[]
  /** Что человек считает включённым: свёрнутая группа и выбитая шириной
   *  колонка остаются здесь, иначе панель «Колонки» врала бы галочками. */
  wanted: ReadonlySet<string>
  /** Колонки, выбитые нехваткой ширины окна — панель «Колонки» пишет причину. */
  hiddenByWidth: string[]
  totalWidth: number
  /** Смещения sticky-колонок, посчитанные один раз из фиксированных ширин. */
  leftOffset: Map<string, number>
  rightOffset: Map<string, number>
  /** Колонка зоны ответственности, подсвеченная в шапке (§4.9). */
  myColumn: string | null
}

export function resolveColumns(options: ResolveOptions): ResolvedColumns {
  const {
    catalog, chosen, collapsedGroups, areaFields, area, pinnedLeftExtra, available, channel,
    order = null,
  } = options
  const byCode = new Map(catalog.map((column) => [column.code, column]))
  // «Моя колонка» есть только у узкой зоны: у админа и собственника зона —
  // «все поля», и закреплять из неё первое попавшееся нечестно (§10.4).
  const myColumn = area !== null && area !== 'admin' ? areaFields[0] ?? null : null

  // 1. Базовый набор: выбор человека либо набор по умолчанию канала.
  const manual = chosen !== null
  let wanted = new Set(manual ? chosen : defaultVisible(channel, catalog))
  for (const column of catalog) if (!column.optional) wanted.add(column.code)

  /* 2. Раньше здесь стояли готовые «планшетные» наборы: ниже 1280 px таблица
     разом теряла половину колонок, а ниже 1024 — оставалась с тремя. Наборы
     не зависели от реальной ширины, и на экране в 1000 px выходило нелепо:
     видно «Бренд · Тир · Категории», а справа три четверти пустого места.
     Теперь колонки уступают место по одной и ровно пока не поместятся — этим
     занимается шаг 4. */

  // 3. Свёрнутые группы → одна колонка-индикатор 28 px на группу (§4.2).
  const visible: ColumnDef[] = []
  const foldedSeen = new Set<ColumnGroupCode>()
  for (const column of catalog) {
    if (!wanted.has(column.code)) continue
    if (collapsedGroups.has(column.group) && column.group !== 'base') {
      if (foldedSeen.has(column.group)) continue
      foldedSeen.add(column.group)
      visible.push(foldColumn(column.group))
      continue
    }
    visible.push(column)
  }

  /* 4. Колонки по ширине больше не выбиваются.
     Так было задумано (§4.2): на узком экране таблица прятала «лишнее». На
     деле это читалось как пропажа данных — человек видит три колонки вместо
     одиннадцати и не понимает, куда делось остальное. Пояснение жило в меню
     «Колонки», куда никто не заходит.
     Теперь набор не зависит от размера окна: не поместилось — таблица
     прокручивается вбок. Это предсказуемо, а убрать лишние колонки можно
     руками — в тех же «Колонках». */
  const hiddenByWidth: string[] = []
  const laid = visible

  // 5. «Моя колонка» встаёт сразу после «Бренда» и закрепляется (§4.9);
  //    иначе закрепляется одна колонка, выбранная человеком вручную.
  const promoted = myColumn ?? pinnedLeftExtra
  const ordered = promoted ? promote(laid, promoted) : laid

  // Колонки идут группами и в порядке GROUP_ORDER: иначе «Ссылка» вклинивалась
  // между колонками группы «Бренд» просто потому, что так объявлена в каталоге.
  // Поверх порядка по умолчанию ложится личный — тот, что человек выставил
  // перетаскиванием в меню «Колонки».
  const groupRank = new Map(
    applyOrder(order?.groups ?? [], groupOrder(catalog)).map((group, index) => [group, index]),
  )
  /* Внутри группы «Бренд» порядок задан явно: тир идёт раньше видимости. */
  const BASE_ORDER = ['gutter', 'brand', 'tier', 'visibility']
  const inGroup = (column: ColumnDef) => {
    const personal = order?.columns[column.group]
    if (personal && personal.length > 0) {
      const index = personal.indexOf(column.code)
      /* Колонка, которой не было в момент перетаскивания, встаёт после
         переставленных, а не в начало группы. */
      if (index !== -1) return index
      return personal.length + catalog.findIndex((item) => item.code === column.code)
    }
    const index = BASE_ORDER.indexOf(column.code)
    return index === -1 ? BASE_ORDER.length : index
  }
  const grouped = [...ordered].sort((a, b) => (
    (groupRank.get(a.group) ?? 0) - (groupRank.get(b.group) ?? 0) || inGroup(a) - inGroup(b)
  ))

  /* Чекбокс и название бренда закреплены слева и всегда идут первыми: если бы
     их можно было утащить в середину, закреплённая зона разорвалась бы. */
  grouped.sort((a, b) => headRank(a) - headRank(b))

  /* Закреплены слева: чекбокс-шеврон, бренд, видимость и тир — при
     горизонтальной прокрутке они всегда на виду. Остальные колонки уезжают. */
  const PINNED_LEFT = new Set(['gutter', 'brand', 'tier', 'visibility'])
  const sized = stretchToWidth(grouped, available).map((column, index, all) => ({
    ...column,
    pinned: PINNED_LEFT.has(column.code) ? ('left' as const) : null,
    groupStart: index > 0 && all[index - 1]!.group !== column.group,
  }))
  const leftOffset = new Map<string, number>()
  const rightOffset = new Map<string, number>()
  let left = 0
  for (const column of sized) {
    if (column.pinned !== 'left') continue
    leftOffset.set(column.code, left)
    left += column.width
  }
  /* Правого закрепления в карточной подаче нет. */

  void byCode
  return {
    columns: sized, wanted, hiddenByWidth, totalWidth: sumWidth(sized),
    leftOffset, rightOffset, myColumn,
  }
}

/** Колонка зоны / закреплённая вручную переезжает за «Бренд» и получает sticky. */
function promote(columns: ColumnDef[], code: string): ColumnDef[] {
  const index = columns.findIndex((column) => column.code === code)
  if (index < 0) return columns
  const target = columns[index]
  if (!target) return columns
  const rest = columns.filter((_, i) => i !== index)
  const brandAt = rest.findIndex((column) => column.code === 'brand')
  const pinned: ColumnDef = { ...target, pinned: 'left', group: 'base' }
  rest.splice(brandAt + 1, 0, pinned)
  return rest
}

function foldColumn(group: ColumnGroupCode): ColumnDef {
  return {
    code: `fold:${group}`,
    title: GROUP_SHORT[group] ?? GROUP_TITLE[group] ?? group,
    srTitle: GROUP_TITLE[group] ?? group,
    width: 28, align: 'center', group, pinned: null, role: 'groupFold', editor: 'none',
    source: { kind: 'none' }, optional: false, channelLabelled: false,
    inBrandRow: true, inCategoryRow: false, hint: GROUP_HINT[group], foldedGroup: group,
  }
}

export function sumWidth(columns: readonly ColumnDef[]): number {
  let total = 0
  for (const column of columns) total += column.width
  return total
}

/** Колонки группы в каталоге — для индикатора свёрнутой группы и тултипа. */
export function groupColumns(catalog: ColumnDef[], group: ColumnGroupCode): ColumnDef[] {
  return catalog.filter((column) => column.group === group && column.optional)
}
